import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  api,
  apiExt,
  downloadAuthed,
  fetchIntentBreakdown,
  fetchReport,
  fetchResolutionTimes,
  isUnsupported,
  type CsatComment,
  type AiQualitySummary,
} from '../lib/api';
import { TopBar } from '../components/TopBar';
import { useAuth } from '../hooks/useAuth';
import { t, useLocale } from '../lib/i18n';

interface ConversationVolume {
  total: number;
  byStatus: Record<string, number>;
  byChannel: Record<string, number>;
  byInbox: Record<string, number>;
  byPriority: Record<string, number>;
  /** Conversations per label. Optional on purpose: a server that predates the
   *  tagging report omits the field entirely, and an absent breakdown must not
   *  render as a card full of zeros — that would read as "nothing was tagged"
   *  when the AI in fact tags everything. */
  byLabel?: Record<string, number>;
}

interface AgentRow {
  agentUid: string;
  /** Server-side join against the `agents` directory. Optional on purpose: a
   *  server that predates the join omits it entirely, and this table must
   *  still render. */
  displayName?: string;
  email?: string;
  resolved: number;
  assigned: number;
  avgResponseMin: number | null;
  avgResolutionMin: number | null;
}

/**
 * Client-side fallback labels for the platform's pseudo-actors, used only when
 * the server sent no displayName (older deploy). Wording matches the server's
 * own SYSTEM_ACTOR_LABELS so the same row can't be called two different things
 * depending on which build answered.
 */
const SYSTEM_ACTOR_LABELS: Record<string, string> = {
  'system:auto': 'Auto-resolve (system)',
  'system:ai': 'AI assistant',
  'system:freshchat': 'Freshchat import',
  'system:escalation': 'Escalation (system)',
  'system:csat': 'CSAT survey (system)',
};

/**
 * What to SHOW for an agent row. Never a bare uid when anything better exists:
 * server name → server email → system-actor label → uid. The uid is still worth
 * showing as a last resort — a row with real numbers on it must not vanish just
 * because the directory has no entry.
 */
function agentLabel(a: AgentRow): string {
  const name = a.displayName?.trim();
  if (name && name !== a.agentUid) return name;
  const email = a.email?.trim();
  if (email) return email;
  if (SYSTEM_ACTOR_LABELS[a.agentUid]) return SYSTEM_ACTOR_LABELS[a.agentUid];
  if (a.agentUid.startsWith('system:')) {
    const suffix = a.agentUid.slice('system:'.length);
    return suffix ? `${suffix} (system)` : 'System';
  }
  return a.agentUid;
}

interface CsatSummary {
  count: number;
  avg: number | null;
  byScore: Record<number, number>;
}

const RANGES: Array<{ key: 'reports.range.7' | 'reports.range.30' | 'reports.range.90'; days: number }> = [
  { key: 'reports.range.7', days: 7 },
  { key: 'reports.range.30', days: 30 },
  { key: 'reports.range.90', days: 90 },
];

const BUCKETS: Array<{ key: string; max: number }> = [
  { key: 'reports.histogram.lt1h', max: 60 * 60 * 1000 },
  { key: 'reports.histogram.1to4h', max: 4 * 60 * 60 * 1000 },
  { key: 'reports.histogram.4to24h', max: 24 * 60 * 60 * 1000 },
  { key: 'reports.histogram.1to3d', max: 3 * 24 * 60 * 60 * 1000 },
  { key: 'reports.histogram.gt3d', max: Infinity },
];

function bucketize(times: number[]): number[] {
  const counts = BUCKETS.map(() => 0);
  for (const ms of times) {
    for (let i = 0; i < BUCKETS.length; i++) {
      if (ms <= BUCKETS[i].max) {
        counts[i]++;
        break;
      }
    }
  }
  return counts;
}

function computeNps(byScore: Record<number, number>): number | null {
  // NPS computed when scores are 0-10. Promoters 9-10, detractors 0-6.
  const max = Math.max(...Object.keys(byScore).map(Number).filter((n) => Number.isFinite(n)), 0);
  if (max < 8) return null; // looks like 1-5 CSAT, not NPS
  let promoters = 0;
  let detractors = 0;
  let total = 0;
  for (const [k, v] of Object.entries(byScore)) {
    const score = Number(k);
    const count = Number(v) || 0;
    total += count;
    if (score >= 9) promoters += count;
    else if (score <= 6) detractors += count;
  }
  if (total === 0) return null;
  return Math.round(((promoters - detractors) / total) * 100);
}

export function ReportsPage() {
  useLocale();
  const { user, claims, signOut } = useAuth();
  const [days, setDays] = useState(30);
  const [downloading, setDownloading] = useState(false);
  // FX-8 made GET /agent/conversations-export admin-only and audited
  // (gdpr.bulk_export). The button stayed visible for every agent, so a support
  // agent's only feedback was a raw 403 in an alert() — a control that exists
  // but always refuses reads as a broken page, not as a permission boundary.
  // Same admin test the server applies (routes/agent.ts isAdminRequest).
  const canExport = claims?.role === 'tenant_admin' || claims?.role === 'super_admin';

  async function exportAll() {
    if (!claims?.tenantCode) return;
    // Hiding a control is presentation, not authorization. The server is the
    // gate (FX-8); this just refuses to issue a request that can only 403.
    if (!canExport) return;
    setDownloading(true);
    try {
      const fromMs = Date.now() - days * 24 * 60 * 60 * 1000;
      const toMs = Date.now();
      const path = api.conversationsExportUrl(claims.tenantCode, { fromMs, toMs, limit: 2000 }).split('//')[1].split('/').slice(1).join('/');
      // downloadAuthed wants a relative path under apiBase. Build it directly.
      const url = `/agent/conversations-export?tenantCode=${encodeURIComponent(claims.tenantCode)}&fromMs=${fromMs}&toMs=${toMs}&limit=2000`;
      void path; // unused — kept for clarity
      await downloadAuthed(url, `chatolam-${claims.tenantCode}-last-${days}d.ndjson`);
    } catch (e) {
      alert(`Export failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setDownloading(false);
    }
  }
  const [vol, setVol] = useState<ConversationVolume | null>(null);
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [csat, setCsat] = useState<CsatSummary | null>(null);
  const [resolutionTimes, setResolutionTimes] = useState<number[]>([]);
  const [intentCounts, setIntentCounts] = useState<Record<string, number>>({});
  const [csatComments, setCsatComments] = useState<CsatComment[]>([]);
  const [aiq, setAiq] = useState<AiQualitySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    const range = { fromMs: Date.now() - days * 24 * 60 * 60 * 1000, toMs: Date.now() };
    Promise.all([
      fetchReport<ConversationVolume>('conversations', range),
      fetchReport<{ items: AgentRow[] }>('agents', range),
      fetchReport<CsatSummary>('csat', range),
      fetchResolutionTimes(range),
      fetchIntentBreakdown(range),
      apiExt.listCsatComments(range.fromMs, range.toMs, 5),
      fetchReport<AiQualitySummary>('ai-quality', range).catch(() => null),
    ])
      .then(([v, a, c, rt, intents, comments, q]) => {
        setVol(v);
        setAgents(a.items);
        setCsat(c);
        setResolutionTimes(rt);
        setIntentCounts(intents);
        setCsatComments(isUnsupported(comments) ? [] : comments.items);
        setAiq(q);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [days]);

  const histogram = bucketize(resolutionTimes);
  const npsValue = csat ? computeNps(csat.byScore) : null;
  const avgCommentLen =
    csatComments.length > 0
      ? Math.round(csatComments.reduce((s, c) => s + (c.comment?.length ?? 0), 0) / csatComments.length)
      : 0;

  // Same defect Settings had: this page dropped the global nav for a lone
  // back-link. TopBar is `fixed` and 40px (h-10) tall, so the scroll container
  // below is offset by that much rather than sitting under it.
  return (
    <>
      <TopBar
        tenantCode={claims?.tenantCode ?? ''}
        userEmail={user?.email ?? undefined}
        onSignOut={signOut}
      />
      <div style={{ padding: 24, marginTop: 40, height: 'calc(100vh - 40px)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h1 style={{ margin: 0, fontSize: 22 }}>{t('reports.title')}</h1>
          <Link to="/inbox" style={{ fontSize: 13, color: '#6b7280' }}>← {t('settings.backToInbox')}</Link>
        </div>
        <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
          {RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setDays(r.days)}
              style={{
                padding: '6px 12px',
                background: days === r.days ? '#2563eb' : 'white',
                color: days === r.days ? 'white' : '#374151',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              {t(r.key)}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          {canExport && (
            <button
              onClick={exportAll}
              disabled={downloading}
              style={{
                padding: '6px 12px',
                background: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                cursor: downloading ? 'wait' : 'pointer',
                fontSize: 13,
              }}
              title={t('reports.export')}
            >
              {downloading ? t('common.loading') : t('reports.export')}
            </button>
          )}
        </div>
        {loading && <div style={{ color: '#6b7280' }}>{t('common.loading')}</div>}
        {err && <div style={{ color: '#dc2626' }}>{t('common.error')}: {err}</div>}

        {/* Degraded: the server could not run the ai_decisions scan (missing or
            still-building collection-group index). Zeros here would read as "the
            AI answered nothing", so say "unknown" out loud instead. */}
        {aiq?.degraded && (
          <div
            style={{
              marginBottom: 24,
              padding: '10px 12px',
              fontSize: 13,
              color: '#92400e',
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 6,
            }}
          >
            AI quality is unavailable for this range — the per-turn scan could not run (missing or
            still-building index). This is not a report of zero AI activity.
          </div>
        )}

        {aiq && !aiq.degraded && aiq.turns > 0 && (
          <section style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 16, marginBottom: 4 }}>AI quality</h2>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
              {aiq.turns.toLocaleString()} AI turns in range{aiq.truncated ? ' (capped at 2,000)' : ''} · from per-turn ai_decisions
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              <Stat label="Auto-answered" value={`${aiq.successRate}%`} />
              <Stat label="Escalation rate" value={`${aiq.escalationRate}%`} />
              <Stat label="Held for agent" value={aiq.suggested.toString()} />
              <Stat label="👎 Flagged bad" value={aiq.badFeedback.toString()} />
            </div>
            <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              <Stat label="Delivered" value={aiq.delivered.toString()} />
              <Stat label="Escalated" value={aiq.escalated.toString()} />
              <Stat label="Failed faithfulness" value={aiq.unfaithful.toString()} />
              <Stat label="👍 Marked good" value={aiq.goodFeedback.toString()} />
            </div>
            {aiq.byIntent.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <Breakdown title="By intent" data={Object.fromEntries(aiq.byIntent.map((x) => [x.intent, x.count]))} />
              </div>
            )}
            {aiq.badFeedback > 0 && (
              <div style={{ marginTop: 8, fontSize: 13 }}>
                <Link to="/inbox" style={{ color: '#dc2626' }}>
                  Review {aiq.badFeedback} flagged answer{aiq.badFeedback === 1 ? '' : 's'} in the “AI Flagged” inbox tab →
                </Link>
              </div>
            )}
          </section>
        )}

        {vol && (
          <section style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 16, marginBottom: 12 }}>{t('reports.volume.title')}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              <Stat label={t('reports.volume.total')} value={vol.total.toString()} />
              <Stat label={t('reports.volume.open')} value={String(vol.byStatus.open ?? 0)} />
              <Stat label={t('reports.volume.pending')} value={String(vol.byStatus.pending ?? 0)} />
              <Stat label={t('reports.volume.resolved')} value={String(vol.byStatus.resolved ?? 0)} />
            </div>
            <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Breakdown title={t('reports.volume.byChannel')} data={vol.byChannel} />
              <Breakdown title={t('reports.volume.byPriority')} data={vol.byPriority} />
            </div>
            {/* What the tags are actually FOR: the volume behind each label, so
                "eSIM is a third of our week" is a number and not a hunch. Full
                width — a tenant can define far more labels than channels, and a
                half-width column truncates the long tail. English literal like
                the "By intent" card above; i18n.ts has no key for it yet. */}
            {vol.byLabel && (
              <div style={{ marginTop: 12 }}>
                <Breakdown title="By label" data={vol.byLabel} />
              </div>
            )}
          </section>
        )}

        <section style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 16, marginBottom: 12 }}>{t('reports.histogram.title')}</h2>
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
            {resolutionTimes.length === 0 && (
              <div style={{ color: '#9ca3af', fontSize: 13 }}>{t('common.noData')}</div>
            )}
            {resolutionTimes.length > 0 && (
              <>
                {BUCKETS.map((b, i) => {
                  const max = Math.max(...histogram, 1);
                  return (
                    <div key={b.key} style={{ marginBottom: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span>{t(b.key)}</span>
                        <span>{histogram[i]}</span>
                      </div>
                      <div style={{ height: 6, background: '#f3f4f6', borderRadius: 3, marginTop: 2 }}>
                        <div
                          style={{
                            height: '100%',
                            width: `${(histogram[i] / max) * 100}%`,
                            background: '#2563eb',
                            borderRadius: 3,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </section>

        <section style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 16, marginBottom: 12 }}>{t('reports.intent.title')}</h2>
          <Breakdown title={t('reports.intent.title')} data={intentCounts} />
        </section>

        <section style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 16, marginBottom: 12 }}>{t('reports.agents.title')}</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left', color: '#6b7280' }}>
                <th style={th}>{t('reports.agents.agent')}</th>
                <th style={th}>{t('reports.agents.assigned')}</th>
                <th style={th}>{t('reports.agents.resolved')}</th>
                <th style={th}>{t('reports.agents.avgFirst')}</th>
                <th style={th}>{t('reports.agents.avgResolution')}</th>
              </tr>
            </thead>
            <tbody>
              {agents.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: 12, color: '#9ca3af' }}>{t('common.noData')}</td>
                </tr>
              )}
              {agents.map((a) => (
                <tr key={a.agentUid} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  {/* Name, not a truncated uid. The uid stays reachable on hover
                      so a row is still identifiable when two agents share a name. */}
                  <td style={td} title={a.agentUid}>{agentLabel(a)}</td>
                  <td style={td}>{a.assigned}</td>
                  <td style={td}>{a.resolved}</td>
                  <td style={td}>{a.avgResponseMin?.toFixed(1) ?? '—'}</td>
                  <td style={td}>{a.avgResolutionMin?.toFixed(1) ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {csat && (
          <section>
            <h2 style={{ fontSize: 16, marginBottom: 12 }}>{t('reports.csat.title')}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              <Stat label={t('reports.csat.responses')} value={String(csat.count)} />
              <Stat label={t('reports.csat.avg')} value={csat.avg ? csat.avg.toFixed(2) : '—'} />
              <Stat label={t('reports.csat.fiveStar')} value={String(csat.byScore[5] ?? 0)} />
              <Stat label={t('reports.csat.nps')} value={npsValue === null ? '—' : String(npsValue)} />
            </div>
            {csatComments.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                  <Stat label={t('reports.csat.avgLen')} value={`${avgCommentLen}`} />
                </div>
                <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                    {t('reports.csat.recentComments')}
                  </div>
                  {csatComments.map((c) => (
                    <div key={c.id} style={{ borderBottom: '1px solid #f3f4f6', padding: '6px 0' }}>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>
                        {new Date(c.createdAt).toLocaleString()}
                        {typeof c.score === 'number' ? ` · ${c.score}` : ''}
                      </div>
                      <div style={{ fontSize: 13, color: '#374151' }}>{c.comment}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

function Breakdown({ title, data }: { title: string; data: Record<string, number> }) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const max = entries[0]?.[1] ?? 1;
  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>{title}</div>
      {entries.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13 }}>No data</div>}
      {entries.map(([k, v]) => (
        <div key={k} style={{ marginBottom: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span>{k}</span>
            <span>{v}</span>
          </div>
          <div style={{ height: 6, background: '#f3f4f6', borderRadius: 3, marginTop: 2 }}>
            <div style={{ height: '100%', width: `${(v / max) * 100}%`, background: '#2563eb', borderRadius: 3 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

const th: React.CSSProperties = { padding: '6px 4px', fontWeight: 500 };
const td: React.CSSProperties = { padding: '6px 4px' };
